/**
 * WHAT "THREE DAYS" MEANS (spec §62D).
 *
 * A schedule that mixes calendar days and working days is a schedule nobody
 * can check, so the rule is written down once, here, and every date in the
 * application goes through it:
 *
 *   • A date is a plain ISO calendar date, `YYYY-MM-DD`. No times, no time
 *     zones, no "midnight in which country" — a renovation task does not
 *     start at 09:00 UTC, it starts on Tuesday.
 *   • A duration is a WHOLE NUMBER OF WORKING DAYS, and it is INCLUSIVE: a
 *     task of 3 days starting Friday occupies Friday, Monday, Tuesday and
 *     finishes on Tuesday.
 *   • A successor starts on the next working day AFTER its predecessor
 *     finishes.
 *   • In workday mode the working days are Monday to Friday. A date that
 *     lands on a weekend rolls FORWARD to Monday, always, so the same
 *     inputs always produce the same schedule.
 *   • In calendar mode every day is a working day. Nothing else changes.
 *
 * There is deliberately no holiday calendar and no per-crew calendar: a
 * flip's schedule is not improved by pretending to know which days a
 * subcontractor takes off (§62D).
 *
 * Pure arithmetic on integers — no Date parsing of user strings, no locale.
 */

export type ScheduleCalendar = "workdays" | "calendar-days";

const DAY_MS = 86_400_000;

/** Is this a real `YYYY-MM-DD`? */
export function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const time = Date.parse(`${value}T00:00:00Z`);
  if (Number.isNaN(time)) return false;
  return toIso(time) === value;
}

function toIso(timeMs: number): string {
  return new Date(timeMs).toISOString().slice(0, 10);
}

function toTime(date: string): number {
  const time = Date.parse(`${date}T00:00:00Z`);
  if (Number.isNaN(time)) throw new Error(`Not a date: ${date}`);
  return time;
}

/** 0 = Sunday … 6 = Saturday, in UTC so it never shifts. */
function weekday(date: string): number {
  return new Date(toTime(date)).getUTCDay();
}

export function isWorkingDay(date: string, calendar: ScheduleCalendar): boolean {
  if (calendar === "calendar-days") return true;
  const day = weekday(date);
  return day !== 0 && day !== 6;
}

/** The same day if it is a working day, otherwise the next one. */
export function nextWorkingDay(date: string, calendar: ScheduleCalendar): string {
  let current = date;
  let guard = 0;
  while (!isWorkingDay(current, calendar)) {
    current = addCalendarDays(current, 1);
    if ((guard += 1) > 10) throw new Error("No working day found");
  }
  return current;
}

export function addCalendarDays(date: string, days: number): string {
  return toIso(toTime(date) + days * DAY_MS);
}

/**
 * The finish of a task that starts on `start` and lasts `durationDays`
 * working days, inclusive. Duration 0 (a milestone) finishes the day it
 * starts and consumes nothing.
 */
export function finishOf(start: string, durationDays: number, calendar: ScheduleCalendar): string {
  const from = nextWorkingDay(start, calendar);
  if (durationDays <= 1) return from;
  let remaining = durationDays - 1;
  let current = from;
  let guard = 0;
  while (remaining > 0) {
    current = addCalendarDays(current, 1);
    if (isWorkingDay(current, calendar)) remaining -= 1;
    if ((guard += 1) > 100_000) throw new Error("Duration too long to schedule");
  }
  return current;
}

/** The day work may start after something finished on `date`. */
export function dayAfter(date: string, calendar: ScheduleCalendar): string {
  return nextWorkingDay(addCalendarDays(date, 1), calendar);
}

/**
 * The start date that finishes on `finish` after `durationDays` working
 * days — the backward pass's mirror of `finishOf`.
 */
export function startFrom(finish: string, durationDays: number, calendar: ScheduleCalendar): string {
  const to = previousWorkingDay(finish, calendar);
  if (durationDays <= 1) return to;
  let remaining = durationDays - 1;
  let current = to;
  let guard = 0;
  while (remaining > 0) {
    current = addCalendarDays(current, -1);
    if (isWorkingDay(current, calendar)) remaining -= 1;
    if ((guard += 1) > 100_000) throw new Error("Duration too long to schedule");
  }
  return current;
}

export function previousWorkingDay(date: string, calendar: ScheduleCalendar): string {
  let current = date;
  let guard = 0;
  while (!isWorkingDay(current, calendar)) {
    current = addCalendarDays(current, -1);
    if ((guard += 1) > 10) throw new Error("No working day found");
  }
  return current;
}

/** The day before work may start on `date`. */
export function dayBefore(date: string, calendar: ScheduleCalendar): string {
  return previousWorkingDay(addCalendarDays(date, -1), calendar);
}

/**
 * Working days from `from` to `to`, counting `from` as day zero. Negative
 * when `to` is earlier. This is how float is measured, so that "3 days of
 * flexibility" means three days somebody could actually work.
 */
export function workingDaysBetween(from: string, to: string, calendar: ScheduleCalendar): number {
  if (from === to) return 0;
  const forward = toTime(to) > toTime(from);
  let count = 0;
  let current = from;
  let guard = 0;
  while (current !== to) {
    current = addCalendarDays(current, forward ? 1 : -1);
    if (isWorkingDay(current, calendar)) count += 1;
    if ((guard += 1) > 100_000) throw new Error("Dates too far apart");
  }
  return forward ? count : -count;
}

/** Plain-language duration: "3 days", "1 day", "milestone". */
export function describeDuration(durationDays: number): string {
  if (durationDays <= 0) return "milestone";
  return `${durationDays} day${durationDays === 1 ? "" : "s"}`;
}
